import {
  SETTING_DEFINITIONS,
  isOwnerAccountSetting,
  isOwnerOnlySetting,
  minorToInput,
  parseMoneyToMinor,
  settingDefinition,
} from '@didto/domain';
import type { SettingKey, SettingValue } from '@didto/domain';
import {
  Button,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Section,
  SegmentedControl,
  Toggle,
} from '@didto/ui';
import type { ThemePreference } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { setThemePreference, useThemePreference } from '@/app/theme';
import { useCommand } from '@/app/useCommand';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import './settings.css';

const SECTIONS: Array<{ title: string; keys: SettingKey[] }> = [
  {
    title: 'Orders and payments',
    keys: [
      'orders.require_payment_before_send',
      'orders.auto_send_after_payment',
      'payments.allow_partial_on_regular_orders',
    ],
  },
  {
    title: 'Kitchen display',
    keys: [
      'kds.warn_seconds',
      'kds.late_seconds',
      'kds.undo_window_seconds',
      'kds.chime_new_ticket',
    ],
  },
  {
    title: 'Expo and serving',
    keys: ['serve.undo_window_seconds', 'expo.warn_seconds', 'expo.late_seconds'],
  },
  {
    title: 'Shifts and cash',
    // cash.safe_enabled stays off and hidden until a safe is in use (Phase 7A-2D)
    keys: [
      'cash.standard_float_minor',
      'cash.count_tolerance_minor',
      'cash.payout_review_minor',
      'shift.takeover_idle_minutes',
      'shift.max_open_hours',
      'shift.closing_max_minutes',
      'shift.late_entry_hours',
    ],
  },
  {
    title: 'Owner review',
    keys: [
      'cash.reconcile_due_hours',
      'review.shortfall_week_minor',
      'review.reconcile_from',
      'security.owner_reauth_seconds',
    ],
  },
  {
    // ADR-034: what the owner's review lists as an alert
    title: 'Review alerts',
    keys: [
      'review.alert_no_sale_minutes',
      'review.alert_near_limit_percent',
      'review.alert_near_limit_count',
    ],
  },
  {
    // ADR-031 (D1, D3): the cashier writes off alone within these; beyond them the owner approves
    title: 'Write-offs',
    keys: [
      'writeoff.walkout_max_minor',
      'writeoff.daily_max_minor',
      'writeoff.daily_max_count',
      'writeoff.staff_meal_daily_minor',
    ],
  },
  {
    // ADR-035: off by default; on, a cashier gives money back alone within these limits
    title: 'Cashier allowance',
    keys: [
      'refund.cashier_allowance',
      'refund.cashier_order_max_minor',
      'refund.cashier_daily_max_minor',
    ],
  },
  { title: 'Business day', keys: ['business.day_start_time'] },
  {
    title: 'PIN security',
    keys: [
      'security.pin_max_attempts',
      'security.pin_lockout_seconds',
      'security.approval_grant_seconds',
    ],
  },
  {
    title: 'Terminal idle lock (seconds)',
    keys: [
      'terminal.idle_lock_seconds.pos',
      'terminal.idle_lock_seconds.kds',
      'terminal.idle_lock_seconds.expo',
      'terminal.idle_lock_seconds.admin',
    ],
  },
  {
    title: 'Device health',
    keys: ['terminal.heartbeat_seconds', 'terminal.online_threshold_seconds'],
  },
];

export function SettingsPage() {
  const s = useSession();
  const canManage = s.can('settings.manage');
  // ADR-037: every change here is a command; none starts while the server cannot be reached
  const online = useOnline();
  // ADR-031: the write-off limits are the business owner's own, from their signed-in account
  const isOwnerAccount =
    s.me?.principal === 'human' &&
    !!s.me.employee &&
    (s.me.owner_employee_ids ?? []).includes(s.me.employee.id);
  const businessId = s.me?.business?.id ?? '';
  const cmd = useCommand([['business-settings'], ['business']]);

  const business = useQuery({
    queryKey: ['business', businessId],
    enabled: !!businessId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business')
        .select('id, name, legal_name, timezone, currency')
        .eq('id', businessId)
        .single();
      if (error) throw error;
      return data;
    },
  });
  const settings = useQuery({
    queryKey: ['business-settings', businessId],
    enabled: !!businessId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_setting')
        .select('key, value')
        .eq('business_id', businessId);
      if (error) throw error;
      return data.map((r) => ({ key: r.key, value: r.value as SettingValue }));
    },
  });
  const valueOf = (key: SettingKey): SettingValue => {
    const row = settings.data?.find((r) => r.key === key);
    return row ? row.value : (settingDefinition(key)!.default as SettingValue);
  };
  const set = (key: SettingKey, value: SettingValue) =>
    cmd.mutateAsync({ kind: 'setting.set', payload: { scope: 'business', key, value } });

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        subtitle="How this device looks, the business profile and the rules the café runs by. Every change to a rule is recorded."
      />
      <AppearanceSection />
      <nav className="d-actions" aria-label="More configuration">
        <Link to="/settings/roles" className="d-btn d-btn--ghost d-btn--sm">
          Roles &amp; permissions
        </Link>
        <Link to="/settings/stations" className="d-btn d-btn--ghost d-btn--sm">
          Production stations
        </Link>
        <Link to="/settings/payment-methods" className="d-btn d-btn--ghost d-btn--sm">
          Payment methods
        </Link>
        <Link to="/settings/security" className="d-btn d-btn--ghost d-btn--sm">
          Security &amp; MFA
        </Link>
        {s.can('audit.view') && (
          <Link to="/settings/audit" className="d-btn d-btn--ghost d-btn--sm">
            Audit log
          </Link>
        )}
      </nav>

      <Section title="Business">
        {business.isLoading && <Loading label="Loading the business profile…" />}
        {business.isError && (
          <ErrorState
            title="Could not load the business profile"
            message={errorText(business.error)}
            onRetry={() => void business.refetch()}
          />
        )}
        {business.data && (
          <BusinessForm
            business={business.data}
            location={s.me?.locations[0] ?? null}
            disabled={!canManage}
            online={online}
            onSave={async (v) => {
              await cmd.mutateAsync({ kind: 'business.update', payload: v });
            }}
            onSaveLocation={async (v) => {
              await cmd.mutateAsync({ kind: 'location.update', payload: v });
            }}
          />
        )}
      </Section>

      {/* The inputs seed their draft from the stored value, so they wait for the settings to load. */}
      {settings.isLoading && <Loading rows={6} label="Loading settings…" />}
      {settings.isError && (
        <ErrorState
          title="Could not load the settings"
          message={errorText(settings.error)}
          onRetry={() => void settings.refetch()}
        />
      )}
      {settings.isSuccess &&
        SECTIONS.map((sec) => (
          <Section key={sec.title} title={sec.title}>
            <div className="settings__rows">
              {sec.keys.map((key) => {
                const def = SETTING_DEFINITIONS.find((d) => d.key === key)!;
                const value = valueOf(key);
                const inputId = `setting-${key}`;
                // ADR-024: cash and shift rules change only from the owner's signed-in account
                const locked =
                  !canManage ||
                  (isOwnerAccountSetting(key) && s.me?.principal !== 'human') ||
                  (isOwnerOnlySetting(key) && !isOwnerAccount);
                return (
                  <div key={key} className="settings__row" data-testid={`setting-${key}`}>
                    <div className="settings__main">
                      {/* the key stays for whoever needs it (support, the audit log): on hover */}
                      <div className="settings__label" id={`${inputId}-label`} title={key}>
                        {def.label}
                      </div>
                      <div className="settings__desc" id={`${inputId}-help`}>
                        {def.description}
                        {def.money ? ' In pesos.' : ''}
                        {canManage && locked ? (
                          <span className="d-faint">
                            {isOwnerOnlySetting(key)
                              ? ' Changed by the owner only, from their signed-in account.'
                              : ' Changed from the owner’s signed-in account.'}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="settings__control">
                      {def.type === 'boolean' ? (
                        <Toggle
                          checked={value === true}
                          disabled={!online || locked || cmd.isPending}
                          label={value ? 'On' : 'Off'}
                          labelledBy={`${inputId}-label`}
                          describedBy={`${inputId}-help`}
                          onChange={(v) => void set(key, v)}
                        />
                      ) : (
                        <SettingInput
                          key={String(value)}
                          def={def}
                          value={value}
                          disabled={locked}
                          online={online}
                          label={def.label}
                          describedBy={`${inputId}-help`}
                          onSave={(v) => set(key, v)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        ))}
    </div>
  );
}

/** How this device looks (ADR-042): kept on the device, for whoever uses it. Settings needs
 *  settings.view; every login also has the quick switch in the account card. */
function AppearanceSection() {
  const theme = useThemePreference();
  return (
    <Section
      title="Appearance"
      description="Light, dark, or the same as this device's own setting. Kept on this device only: each tablet and phone keeps its own."
      testId="appearance"
    >
      <SegmentedControl<ThemePreference>
        ariaLabel="Theme"
        value={theme}
        options={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
          { value: 'system', label: 'System' },
        ]}
        onChange={setThemePreference}
        testId="theme"
      />
    </Section>
  );
}

function SettingInput({
  def,
  value,
  disabled,
  online,
  label,
  describedBy,
  onSave,
}: {
  def: (typeof SETTING_DEFINITIONS)[number];
  value: SettingValue;
  disabled: boolean;
  online: boolean;
  label: string;
  describedBy: string;
  onSave: (v: SettingValue) => Promise<unknown>;
}) {
  // money settings are stored in centavos and edited in pesos
  const shown = def.money ? minorToInput(Number(value)) : String(value);
  const [draft, setDraft] = useState(shown);
  const dirty = draft !== shown;
  const parsed = def.money
    ? parseMoneyToMinor(draft)
    : def.type === 'integer'
      ? Number(draft)
      : draft;
  const invalid = parsed === null || (typeof parsed === 'number' && !Number.isFinite(parsed));
  return (
    <>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        inputMode={def.money ? 'decimal' : def.type === 'integer' ? 'numeric' : undefined}
        className={`settings__input ${def.money ? 'd-input--money' : 'd-input--num'}`}
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={(dirty && invalid) || undefined}
        data-testid={`input-${def.key}`}
      />
      <Button
        size="sm"
        variant="primary"
        disabled={!online || !dirty || disabled || invalid}
        onClick={() => {
          if (parsed !== null) void onSave(parsed);
        }}
        data-testid={`save-${def.key}`}
      >
        Save
      </Button>
    </>
  );
}

function BusinessForm({
  business,
  location,
  disabled,
  online,
  onSave,
  onSaveLocation,
}: {
  business: { name: string; legal_name: string | null; timezone: string; currency: string };
  location: { id: string; name: string; code: string } | null;
  disabled: boolean;
  online: boolean;
  onSave: (v: { name: string; legal_name: string | null; timezone: string }) => Promise<void>;
  onSaveLocation: (v: { location_id: string; name: string }) => Promise<void>;
}) {
  const [name, setName] = useState(business.name);
  const [legal, setLegal] = useState(business.legal_name ?? '');
  const [tz, setTz] = useState(business.timezone);
  const [locName, setLocName] = useState(location?.name ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="d-stack"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void Promise.all([
          onSave({ name: name.trim(), legal_name: legal.trim() || null, timezone: tz.trim() }),
          location && locName.trim() !== location.name
            ? onSaveLocation({ location_id: location.id, name: locName.trim() })
            : Promise.resolve(),
        ]).finally(() => setBusy(false));
      }}
    >
      <div className="d-grid">
        <Field label="Business name">
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={disabled}
              required
              data-testid="business-name"
            />
          )}
        </Field>
        <Field label="Legal name (optional)">
          {(id) => (
            <Input
              id={id}
              value={legal}
              onChange={(e) => setLegal(e.target.value)}
              disabled={disabled}
            />
          )}
        </Field>
        <Field label="Timezone">
          {(id) => (
            <Input
              id={id}
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              disabled={disabled}
              required
            />
          )}
        </Field>
        <Field label="Location name">
          {(id) => (
            <Input
              id={id}
              value={locName}
              onChange={(e) => setLocName(e.target.value)}
              disabled={disabled || !location}
            />
          )}
        </Field>
      </div>
      <div className="d-actions settings__end">
        <Button
          type="submit"
          variant="primary"
          disabled={disabled || !online}
          busy={busy}
          data-testid="save-business"
        >
          Save profile
        </Button>
      </div>
    </form>
  );
}
